import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MapPin, Phone, Mail, Clock, MessageCircle, CheckCircle, AlertCircle } from 'lucide-react';
import { SEO, getBreadcrumbSchema } from '../components/SEO';
import { trackContactFormSubmission } from '../utils/analytics';
import { HoneypotField } from '../components/HoneypotField';

const API = '/api';

export const ContactPage = () => {
  const { t, language, direction } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [pageContent, setPageContent] = useState({});
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    inquiry_type: 'general',
    website: '',  // Honeypot field
  });

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const response = await fetch(`${API}/cms/page-content?sections=contact_info`);
        if (response.ok) {
          const data = await response.json();
          setPageContent(data);
        }
      } catch (error) {
        console.error('Error fetching page content:', error);
      }
    };
    fetchPageContent();
  }, []);

  // Helper to get CMS content with fallback
  const getContent = (field, fallback) => {
    const section = pageContent.contact_info;
    if (section) {
      const content = language === 'ar' ? section.content_ar : section.content_en;
      if (content && content[field]) {
        return content[field];
      }
    }
    return fallback || '';
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    
    try {
      const response = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setSuccess(true);
        // Track contact form submission in Google Analytics
        trackContactFormSubmission(formData.inquiry_type);
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
          inquiry_type: 'general',
          website: '',  // Reset honeypot
        });
      } else {
        const data = await response.json();
        setError(data.detail || t('forms.error'));
      }
    } catch (error) {
      setError(t('forms.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbSchema = getBreadcrumbSchema([
    { name: language === 'ar' ? 'الرئيسية' : 'Home', url: '/' },
    { name: language === 'ar' ? 'اتصل بنا' : 'Contact' }
  ]);

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="contact-page">
      <SEO
        title="Contact Us - Get in Touch"
        titleAr="اتصل بنا - تواصل معنا"
        description="Contact WeDo HR Services for recruitment support, corporate training inquiries, HR consulting, or career advisory services. Based in Germany, serving Europe, Middle East, GCC, and Africa."
        descriptionAr="تواصل مع ويدو لخدمات الموارد البشرية للحصول على دعم التوظيف أو استفسارات التدريب المؤسسي أو استشارات الموارد البشرية أو خدمات الإرشاد المهني. مقرنا في ألمانيا ونخدم أوروبا والشرق الأوسط ودول الخليج وأفريقيا."
        keywords="contact WeDo, HR services contact, recruitment inquiry, training inquiry, اتصل بويدو, استفسار توظيف"
        canonicalUrl="/contact"
        structuredData={breadcrumbSchema}
        language={language}
      />
      {/* Header */}
      <section className="bg-white border-b border-[#E5E7EB] py-16">
        <div className="container-custom">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#111827] mb-4">
            {getContent('title', t('contact.title'))}
          </h1>
          <p className="text-lg text-[#6B7280] max-w-2xl">
            {getContent('subtitle', t('contact.subtitle'))}
          </p>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              <Card className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="h-6 w-6 text-[#33AADD]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#111827] mb-2">{t('contact.address')}</h3>
                      <p className="text-[#6B7280]">
                        {getContent('company_name', 'WeDo HR Services UG')}<br />
                        {getContent('address_line1', 'Düsseldorf')}, {getContent('address_line2', 'Germany')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="h-6 w-6 text-[#33AADD]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#111827] mb-2">{t('contact.phone')}</h3>
                      <a href={`tel:${getContent('phone', '+4915906453419')}`} className="text-[#33AADD] hover:underline">
                        {getContent('phone_display', '+49 1590 6453419')}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-6 w-6 text-[#33AADD]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#111827] mb-2">{t('contact.email')}</h3>
                      <a href={`mailto:${getContent('email', 'info@wedohr.net')}`} className="text-[#33AADD] hover:underline">
                        {getContent('email', 'info@wedohr.net')}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card-hover">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-[#33AADD]/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="h-6 w-6 text-[#33AADD]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#111827] mb-2">{t('contact.hours')}</h3>
                      <p className="text-[#6B7280]">
                        {getContent('hours', 'Monday - Friday: 8:00 AM - 5:00 PM CET')}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <a 
                href={`https://wa.me/${getContent('whatsapp_number', '4915906453419').replace(/[^0-9]/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                data-testid="whatsapp-link"
              >
                <Card className="bg-green-500 border-none hover:bg-green-600 transition-colors cursor-pointer">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4 text-white">
                      <MessageCircle className="h-6 w-6" />
                      <div>
                        <span className="font-semibold block">{language === 'ar' ? 'تواصل عبر واتساب' : 'Chat on WhatsApp'}</span>
                        <span className="text-sm text-white/80">{getContent('phone_display', '+49 1590 6453419')}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </a>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardContent className="p-8">
                  {success ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                        <CheckCircle className="h-8 w-8 text-green-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-[#111827] mb-2">{t('forms.success')}</h3>
                      <p className="text-[#6B7280] mb-6">
                        Thank you for contacting us! We will get back to you shortly.
                      </p>
                      <Button onClick={() => setSuccess(false)} className="btn-primary">
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Honeypot anti-spam field */}
                      <HoneypotField 
                        value={formData.website} 
                        onChange={(v) => handleChange('website', v)} 
                      />
                      
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">{t('forms.fullName')} *</Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            required
                            className="input-focus"
                            data-testid="input-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email">{t('forms.email')} *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            required
                            className="input-focus"
                            data-testid="input-email"
                          />
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="phone">{t('forms.phone')}</Label>
                          <Input
                            id="phone"
                            value={formData.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                            className="input-focus"
                            data-testid="input-phone"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('forms.inquiryType')}</Label>
                          <Select value={formData.inquiry_type} onValueChange={(v) => handleChange('inquiry_type', v)}>
                            <SelectTrigger data-testid="select-inquiry-type">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="general">{t('forms.general')}</SelectItem>
                              <SelectItem value="recruitment">{t('nav.recruitment')}</SelectItem>
                              <SelectItem value="training">{t('nav.training')}</SelectItem>
                              <SelectItem value="consulting">{t('nav.consulting')}</SelectItem>
                              <SelectItem value="career">{t('nav.careerAdvisory')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="subject">{t('forms.subject')} *</Label>
                        <Input
                          id="subject"
                          value={formData.subject}
                          onChange={(e) => handleChange('subject', e.target.value)}
                          required
                          className="input-focus"
                          data-testid="input-subject"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message">{t('forms.message')} *</Label>
                        <Textarea
                          id="message"
                          value={formData.message}
                          onChange={(e) => handleChange('message', e.target.value)}
                          required
                          rows={6}
                          className="input-focus"
                          data-testid="input-message"
                        />
                      </div>

                      {error && (
                        <div className="flex items-center gap-2 text-red-600 p-3 bg-red-50 rounded-lg">
                          <AlertCircle className="h-5 w-5" />
                          <span>{error}</span>
                        </div>
                      )}

                      <Button 
                        type="submit" 
                        className="w-full btn-primary" 
                        disabled={submitting}
                        data-testid="submit-btn"
                      >
                        {submitting ? t('forms.sending') : t('forms.submit')}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="pb-0">
        <div className="h-96 bg-[#E5E7EB] flex items-center justify-center">
          <div className="text-center">
            <MapPin className="h-16 w-16 text-[#33AADD] mx-auto mb-4" />
            <p className="text-[#6B7280]">Düsseldorf, Germany</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
